// src/pages/Administration/Settings/settingGroups.ts
/**
 * How a module setting is presented: which control it gets, and which card it sits in.
 *
 * Both decisions are inferred from the row itself, because the API exposes **no schema**: every value
 * is stored as a string and there is no type or category field. Inference is deliberately conservative,
 * and anything unrecognised falls into a plain text field in an "Other" group rather than being guessed
 * at. If the backend ever publishes a type and a category per setting, key off those and delete this.
 */

/** Which input a value deserves. */
export type ControlKind = 'boolean' | 'number' | 'url' | 'text';

const BOOLEAN_VALUES = new Set(['true', 'false']);

/**
 * A boolean arrives as `"true"`, an interval as `"24"`. Rendering both as the same wide text box makes
 * an admin hand-type `true`, and a typo there is silent: anything that is not `true` reads as false.
 */
export function controlKindOf(value?: string | null): ControlKind {
  const raw = (value ?? '').trim();
  if (!raw) return 'text';
  if (BOOLEAN_VALUES.has(raw.toLowerCase())) return 'boolean';
  if (/^\d+$/.test(raw)) return 'number';
  if (/^https?:\/\//i.test(raw)) return 'url';
  return 'text';
}

export const isTruthyValue = (value: string) => value.trim().toLowerCase() === 'true';

/**
 * Writes a boolean back in the casing the stored value already used, so a backend that seeded `"True"`
 * does not silently start receiving `"true"` from this screen.
 */
export function writeBoolean(next: boolean, original?: string | null) {
  const wasCapitalised = /^[A-Z]/.test((original ?? '').trim());
  const text = next ? 'true' : 'false';
  return wasCapitalised ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/**
 * Turns a backend key into a field label: `DefaultSessionDuration` and `default_session_duration` both
 * become "Default session duration".
 *
 * ⚠️ A **dotted** key is a scoped setting — `AllowBulkDelete.Intakes` is the bulk-delete switch for one
 * page, not a key called "allow bulk delete.intakes". The scope is split off and kept capitalised, so
 * the four `AllowDelete.*` rows read as one setting per page instead of four near-identical strings
 * that have to be read character by character to tell apart.
 */
export function humaniseKey(key: string) {
  const [base, ...rest] = key.split('.');
  const scope = rest.join(' ').trim();

  const words = (part: string) => part
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();

  const spaced = words(base);
  if (!spaced) return key;
  const label = spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();

  if (!scope) return label;
  const scopeWords = words(scope);
  return `${label} (${scopeWords.charAt(0).toUpperCase() + scopeWords.slice(1)})`;
}

export type GroupId = 'deletion' | 'access' | 'timing' | 'notifications' | 'other';

export interface GroupDef {
  id: GroupId;
  title: string;
  blurb: string;
  /** Mantine colour for the group's `ThemeIcon`. */
  color: string;
}

/** Card order on the page. */
export const SETTING_GROUPS: GroupDef[] = [
  {
    id: 'deletion',
    title: 'Deletion',
    blurb: 'Whether records can be removed from this module, and what must be typed to confirm. '
      + 'Deleting cannot be undone — leave these off unless the office needs them.',
    color: 'red',
  },
  {
    id: 'access',
    title: 'Public access and intake',
    blurb: 'Who can reach the module from outside, and what happens to what they send in.',
    color: 'blue',
  },
  {
    id: 'timing',
    title: 'Reminders and timing',
    blurb: 'How long things may sit, and how far ahead reminders go out.',
    color: 'orange',
  },
  {
    id: 'notifications',
    title: 'Notifications',
    blurb: 'The master switch and the links notifications point at.',
    color: 'violet',
  },
  {
    id: 'other',
    title: 'Other',
    blurb: 'Settings that do not fit the groups above.',
    color: 'gray',
  },
];

/**
 * Sorts a key into a card.
 *
 * ⚠️ **Order matters.** Several keys match more than one pattern, and the first match wins:
 * `IntakePendingReminderHours` is about timing, not intake, and `RegistrationClosingAlertDays` is about
 * timing, not registration. Notifications is checked first because `NotificationsEnabled` and `PortalUrl`
 * are unambiguous.
 *
 * ⚠️ **Deletion is checked before everything.** The per-page keys are named for the page they govern —
 * `AllowDelete.Intakes`, `AllowBulkDelete.Registrations` — so every one of them also matches the
 * `access` pattern below. Without this first, the settings that permit unrecoverable data loss would
 * scatter themselves through "Public access and intake" alongside capacity limits.
 */
export function groupIdOf(key: string): GroupId {
  const k = key.toLowerCase();
  if (/delete|deletion/.test(k)) return 'deletion';
  if (/notification|portal|url|email|sms/.test(k)) return 'notifications';
  if (/reminder|hours|days|alert|stale|expiry|timeout/.test(k)) return 'timing';
  if (/public|approval|intake|participant|registration|request|capacity|max/.test(k)) return 'access';
  return 'other';
}
